import type {
  ListServiceInvoicesCursor,
  ListServiceInvoicesInput,
} from "@redotech/admin-sdk/rpc/schema/service-invoices/list-service-invoices";
import { ServiceInvoiceStatus } from "@redotech/admin-sdk/rpc/schema/service-invoices/service-invoice-status";
import type { DeserializeResult } from "@redotech/react-util/use-synced-state";
import { ServiceInvoiceType } from "@redotech/redo-billing-common/service-invoice";

export interface ServiceInvoiceFiltersState {
  statuses: ServiceInvoiceStatus[];
  kinds: ServiceInvoiceType[];
  teamIds: string[];
}

export const DEFAULT_SERVICE_INVOICE_FILTERS_STATE: ServiceInvoiceFiltersState =
  { statuses: [ServiceInvoiceStatus.FAILED], kinds: [], teamIds: [] };

const STATUSES_PARAM = "statuses";
const KINDS_PARAM = "kinds";
const TEAM_IDS_PARAM = "teamIds";
export const SERVICE_INVOICE_OWNED_KEYS = [
  STATUSES_PARAM,
  KINDS_PARAM,
  TEAM_IDS_PARAM,
];

const DEFAULT_STATUSES_KEY = [...DEFAULT_SERVICE_INVOICE_FILTERS_STATE.statuses]
  .sort()
  .join(",");

function statusesAreDefault(statuses: ServiceInvoiceStatus[]): boolean {
  return [...statuses].sort().join(",") === DEFAULT_STATUSES_KEY;
}

export function serializeServiceInvoiceFilters(
  state: ServiceInvoiceFiltersState,
): URLSearchParams {
  const params = new URLSearchParams();
  if (!statusesAreDefault(state.statuses)) {
    if (state.statuses.length > 0) {
      params.set(STATUSES_PARAM, state.statuses.join(","));
    } else {
      params.set(STATUSES_PARAM, "");
    }
  }
  if (state.kinds.length > 0) {
    params.set(KINDS_PARAM, state.kinds.join(","));
  }
  if (state.teamIds.length > 0) {
    params.set(TEAM_IDS_PARAM, state.teamIds.join(","));
  }
  return params;
}

export function deserializeServiceInvoiceFilters(
  params: URLSearchParams,
): DeserializeResult<ServiceInvoiceFiltersState> {
  const partial: Partial<ServiceInvoiceFiltersState> = {};

  const statusesRaw = params.get(STATUSES_PARAM);
  if (statusesRaw !== null) {
    partial.statuses = statusesRaw
      .split(",")
      .filter(Boolean)
      .filter(isServiceInvoiceStatusFilter);
  }

  const kindsRaw = params.get(KINDS_PARAM);
  if (kindsRaw !== null) {
    partial.kinds = kindsRaw
      .split(",")
      .filter(Boolean)
      .filter(isServiceInvoiceType);
  }

  const teamIdsRaw = params.get(TEAM_IDS_PARAM);
  if (teamIdsRaw !== null) {
    partial.teamIds = teamIdsRaw.split(",").filter(Boolean);
  }

  const hasAnyUrlParams = SERVICE_INVOICE_OWNED_KEYS.some(
    (key) => params.get(key) !== null,
  );

  return {
    hasAnyUrlParams,
    state: { ...DEFAULT_SERVICE_INVOICE_FILTERS_STATE, ...partial },
  };
}

export function buildListServiceInvoicesInput(args: {
  cursor?: ListServiceInvoicesCursor;
  limit: number;
  statuses: ServiceInvoiceStatus[];
  kinds: ServiceInvoiceType[];
  teamIds: string[];
}): ListServiceInvoicesInput {
  return {
    limit: args.limit,
    ...(args.cursor ? { cursor: args.cursor } : {}),
    ...(args.statuses.length > 0 ? { statuses: args.statuses } : {}),
    ...(args.kinds.length > 0 ? { kinds: args.kinds } : {}),
    ...(args.teamIds.length > 0 ? { teamIds: args.teamIds } : {}),
  };
}

function isServiceInvoiceStatusFilter(v: unknown): v is ServiceInvoiceStatus {
  return (
    typeof v === "string" &&
    (Object.values(ServiceInvoiceStatus) as string[]).includes(v)
  );
}

function isServiceInvoiceType(v: unknown): v is ServiceInvoiceType {
  return (
    typeof v === "string" &&
    (Object.values(ServiceInvoiceType) as string[]).includes(v)
  );
}
